import type { AuthUser } from '@/api/models';
import type { AuthMethod } from '@/utils/authSession';

/**
 * Who may see Profit & Dividends.
 *
 * **Every route under `api/v1/ledger/dividends` is `OrgAdmin`, reads included** —
 * the same rule the salaries register follows, for the same reason. Who owns the
 * company and how much profit it may distribute are not branch-level facts: the
 * register returns each shareholder's identifier and holding, and the summary
 * returns the entity's retained earnings. A BranchAdmin is a store manager.
 *
 * The policy also requires `auth_method=password`. A BranchAdmin holding a
 * PIN-issued kiosk token is a BranchAdmin, and still 403s; so does an OrgAdmin on
 * a kiosk token.
 *
 * Deriving it here — rather than letting the page discover it from a 403 — is
 * what lets the screen explain *why* it is unavailable. A raw 403 tells the user
 * nothing they can act on.
 */
export type DividendsAccess =
    /**
     * A PIN/kiosk session. Every dividends endpoint will 403 whatever the role,
     * so the screen must not fetch at all.
     */
    | { kind: 'kiosk' }
    /**
     * Signed in with a password, but not an OrgAdmin. There is nothing to
     * degrade to: no branch-scoped view of the shareholder register exists.
     */
    | { kind: 'forbidden' }
    /** OrgAdmin with a password session: the register, its declarations and its writes. */
    | { kind: 'full' };

/**
 * Derive the access level from the persisted session.
 *
 * Fails closed on every unknown: no user, or an `auth_method` that could not be
 * read, is treated as a kiosk session rather than optimistically rendering a
 * screen whose every request would 403.
 */
export const deriveDividendsAccess = (
    user: AuthUser | null,
    authMethod: AuthMethod | null,
): DividendsAccess => {
    if (!user) {
        return { kind: 'kiosk' };
    }
    if (authMethod !== 'password') {
        return { kind: 'kiosk' };
    }
    return user.role === 'OrgAdmin' ? { kind: 'full' } : { kind: 'forbidden' };
};

/**
 * Whether this access level may read the register at all — which is also
 * whether any query on this screen may run.
 */
export const canReadDividends = (access: DividendsAccess): boolean =>
    access.kind === 'full';

/** The i18n key explaining why the screen is unavailable at this level. */
export const DIVIDENDS_ACCESS_COPY: Record<
    Exclude<DividendsAccess['kind'], 'full'>,
    { titleKey: string; bodyKey: string }
> = {
    kiosk: {
        titleKey: 'dividends.access.kiosk.title',
        bodyKey: 'dividends.access.kiosk.body',
    },
    forbidden: {
        titleKey: 'dividends.access.forbidden.title',
        bodyKey: 'dividends.access.forbidden.body',
    },
};
